import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Link2, Phone, Video, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSDRConnectionMetrics, type ConnectionMetrics } from '@/hooks/useSDRConnectionMetrics';
import { useTranslation } from 'react-i18next';
import { SDRConnectionDetailModal } from './SDRConnectionDetailModal';

interface SelectedSDR {
  id: string;
  name: string;
}

interface SDRConnectionsSectionProps {
  dateRange?: { from: Date | null; to: Date | null };
}

export function SDRConnectionsSection({ dateRange }: SDRConnectionsSectionProps) {
  const { t } = useTranslation();
  const { today, week, month, custom, loading, lastUpdate, refresh } = useSDRConnectionMetrics(dateRange);
  
  // Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSDR, setSelectedSDR] = useState<SelectedSDR | null>(null);

  // Use custom metrics when dateRange is provided (including "All Time" with null dates), otherwise use month
  const isAllTime = dateRange && dateRange.from === null && dateRange.to === null;
  const metrics: ConnectionMetrics = isAllTime || dateRange?.from ? custom : month;

  const handleRowClick = (sdr: { userId: string; userName: string }) => {
    setSelectedSDR({ id: sdr.userId, name: sdr.userName });
    setDetailModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{t('calls.sdrConnections', 'Conexões dos SDRs')}</h3>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && !loading && (
            <span className="text-xs text-muted-foreground">
              {t('calls.metrics.lastUpdate', 'Última atualização')}: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? t('calls.metrics.updating', 'Atualizando...') : t('calls.metrics.update', 'Atualizar')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('calls.totalConnections', 'Total de Conexões')}
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2">{metrics.totalConnections}</p>
              )}
            </div>
            <Link2 className="h-8 w-8 text-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('calls.callTransfers', 'Transferências de Ligação')}
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-blue-600">{metrics.totalCallTransfers}</p>
              )}
            </div>
            <Phone className="h-8 w-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {t('calls.videoConnections', 'Video Calls Conectadas')}
              </p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-green-600">{metrics.totalVideoConnections}</p>
              )}
            </div>
            <Video className="h-8 w-8 text-green-600" />
          </div>
        </Card>
      </div>

      {/* Period Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('common.today', 'Hoje')}</h4>
          {loading ? (
            <Skeleton className="h-6 w-full" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">{today.totalConnections}</span>
              <span className="text-xs text-muted-foreground">
                ({today.totalCallTransfers} <Phone className="h-3 w-3 inline" /> + {today.totalVideoConnections} <Video className="h-3 w-3 inline" />)
              </span>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('common.thisWeek', 'Esta Semana')}</h4>
          {loading ? (
            <Skeleton className="h-6 w-full" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">{week.totalConnections}</span>
              <span className="text-xs text-muted-foreground">
                ({week.totalCallTransfers} <Phone className="h-3 w-3 inline" /> + {week.totalVideoConnections} <Video className="h-3 w-3 inline" />)
              </span>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('common.thisMonth', 'Este Mês')}</h4>
          {loading ? (
            <Skeleton className="h-6 w-full" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold">{month.totalConnections}</span>
              <span className="text-xs text-muted-foreground">
                ({month.totalCallTransfers} <Phone className="h-3 w-3 inline" /> + {month.totalVideoConnections} <Video className="h-3 w-3 inline" />)
              </span>
            </div>
          )}
        </Card>
      </div>

      {/* SDR Table */}
      <Card className="p-6">
        <h4 className="text-lg font-semibold mb-4">{t('calls.connectionsBySDR', 'Conexões por SDR')}</h4>
        {loading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('calls.metrics.user', 'SDR')}</TableHead>
                  <TableHead className="text-right">{t('calls.metrics.total', 'Total')}</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Phone className="h-4 w-4" />
                      {t('calls.transfers', 'Transferências')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Video className="h-4 w-4" />
                      {t('calls.videoCalls', 'Video Calls')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.bySDR && metrics.bySDR.length > 0 ? (
                  metrics.bySDR.map((sdr) => (
                    <TableRow 
                      key={sdr.userId}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleRowClick(sdr)}
                    >
                      <TableCell className="font-medium">{sdr.userName}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{sdr.totalConnections}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-blue-600">{sdr.callTransfers}</TableCell>
                      <TableCell className="text-right text-green-600">{sdr.videoConnections}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      {t('calls.metrics.noDataAvailable', 'Nenhum dado disponível')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedSDR && (
        <SDRConnectionDetailModal
          open={detailModalOpen}
          onOpenChange={setDetailModalOpen}
          sdrName={selectedSDR.name}
          sdrUserId={selectedSDR.id}
          dateRange={dateRange}
        />
      )}
    </div>
  );
}
